import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronsUpDown, Search, X, User } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { dmarc_api } from '../../services/auth';
//import { user_api } from '../../services/auth';

// Mock users for demonstration (No deberían salir si hay conexion)
const MOCK_USERS = [
  { id: '1', name: 'Admin User', email: 'admin@example.com' },
  { id: '2', name: 'Juan Pérez', email: 'juan.perez@empresa.cl' },
  { id: '3', name: 'María García', email: 'm.garcia@tech.com' },
  { id: '4', name: 'Carlos Soto', email: 'csoto@servicios.io' },
  { id: '5', name: 'Ana López', email: 'alopez@consultora.net' },
];

export default function UserSelector({ selectedUsers, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const containerRef = useRef(null);

  const allSelected = selectedUsers?.length === users?.length && users?.length > 0;

  const fetchUsers = async () => {
  try {
    const userList = await dmarc_api.get("/dmarc/client-list");

    if (userList.status === 200) {
      setUsers(userList.data.detail || userList.data || []);
    }
  } catch (error) {
    console.error("ERROR AL TRAER USUARIOS:", error);
  }
};

  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange([...users]);
    }
  };

  useEffect(() => {
    fetchUsers();
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredUsers = users?.filter(user =>
    user?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user?.correo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleUser = (user) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    if (isSelected) {
      onChange(selectedUsers.filter(u => u.id !== user.id));
    } else {
      onChange([...selectedUsers, user]);
    }
  };

  const removeUser = (userId) => {
    onChange(selectedUsers.filter(u => u.id !== userId));
  };

  return (
    <div className="flex items-end gap-3 w-full" ref={containerRef}>
      <div className="flex-1 relative">
        <label className="block text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest mb-1.5">
          Seleccionar Usuarios
        </label>
        
        <div 
          onClick={() => setIsOpen(!isOpen)}
          className="min-h-[42px] w-full px-3 py-2 border border-gray-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900 cursor-pointer flex flex-wrap gap-2 items-center transition-all hover:border-cyan-500/50"
        >
          {selectedUsers?.length === 0 ? (
            <span className="text-sm text-gray-400">Seleccione uno o más usuarios...</span>
          ) : (
            selectedUsers.map(user => (
              <span 
                key={user.id}
                className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 rounded-lg text-xs font-bold border border-cyan-100 dark:border-cyan-500/20"
              >
                {user.nombre}
                <X 
                  size={12} 
                  className="cursor-pointer hover:text-cyan-900 dark:hover:text-white" 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeUser(user.id);
                  }}
                />
              </span>
            ))
          )}
          <ChevronsUpDown size={16} className="ml-auto text-gray-400" />
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl shadow-xl overflow-hidden"
            >
              <div className="p-2 border-b border-gray-100 dark:border-slate-800 flex items-center gap-2">
                <Search size={14} className="text-gray-400" />
                <input 
                  autoFocus
                  type="text"
                  placeholder="Buscar usuario..."
                  className="w-full bg-transparent border-none outline-none text-sm dark:text-slate-200"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="max-h-60 overflow-y-auto p-1">
                {filteredUsers?.length > 0 ? (
                  filteredUsers.map(user => {
                    const isSelected = selectedUsers.some(u => u.id === user.id);
                    return (
                      <div
                        key={user.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleUser(user);
                        }}
                        className={`flex items-center justify-between px-3 py-2 rounded-xl cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400' 
                            : 'hover:bg-gray-50 dark:hover:bg-slate-800 text-gray-700 dark:text-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isSelected ? 'bg-cyan-200 dark:bg-cyan-500/20' : 'bg-gray-100 dark:bg-slate-800'}`}>
                            <User size={14} />
                          </div>
                          <div>
                            <p className="text-xs font-bold">{user.nombre}</p>
                            <p className="text-[10px] opacity-60">{user.correo}</p>
                          </div>
                        </div>
                        {isSelected && <Check size={14} />}
                      </div>
                    );
                  })
                ) : (
                  <div className="p-4 text-center text-xs text-gray-400">No se encontraron usuarios</div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div 
        onClick={toggleAll}
        className="flex items-center gap-2 h-[42px] px-4 border border-gray-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900 cursor-pointer hover:border-cyan-500/50 transition-all group"
      >
        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${allSelected ? 'bg-cyan-500 border-cyan-500' : 'border-gray-300 dark:border-slate-700 group-hover:border-cyan-500'}`}>
          {allSelected && <Check size={10} className="text-white" />}
        </div>
        <span className="text-[10px] font-black text-gray-400 dark:text-slate-500 uppercase tracking-widest whitespace-nowrap">
          Todos
        </span>
      </div>
    </div>
  );
}
